/**
 * CameraPanel - Camera controls
 * Matches the Java SketchChair camera controls.
 * Vertical layout inside the right properties panel: a row of Phosphor
 * icon buttons (Move / Rotate / Zoom) + a bindings hint below.
 */

import { ICONS, phIcon } from '../icons.js';
import { VIEW_PRESETS } from '../../rendering/SceneRotationController.js';

export class CameraPanel {
    constructor(application) {
        this.app = application;
        this.createPanel();
    }

    /**
     * Create the panel DOM structure
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'camera-panel panel';
        panel.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 10px;
            width: 100%;
        `;

        // Camera controls - clicking toggles a mode where LEFT-drag controls
        // the camera instead of drawing (also always available: right-drag
        // rotate, middle-drag pan, scroll zoom)
        const controls = [
            {
                icon: ICONS.arrowsOutCardinal,
                label: 'Move',
                mode: 'pan',
                tooltip: 'Pan the view with left-drag (or middle-drag anytime)'
            },
            {
                icon: ICONS.arrowsClockwise,
                label: 'Rotate',
                mode: 'rotate',
                tooltip: 'Rotate around the chair with left-drag (or right-drag anytime)'
            },
            {
                icon: ICONS.magnifyingGlassPlus,
                label: 'Zoom',
                mode: 'zoom',
                tooltip: 'Zoom with left-drag up/down (or scroll anytime)'
            }
        ];

        const buttonRow = document.createElement('div');
        buttonRow.style.cssText = `
            display: flex;
            align-items: stretch;
            gap: 4px;
        `;

        this.modeButtons = new Map();
        controls.forEach(control => {
            const button = this.createControlButton({
                ...control,
                action: () => {
                    // Toggle: clicking the active mode turns it off
                    const next = this.app.activeCameraMode === control.mode ? null : control.mode;
                    this.app.setCameraMode(next);
                }
            });
            this.modeButtons.set(control.mode, button);
            buttonRow.appendChild(button);
        });
        panel.appendChild(buttonRow);

        // Jump-to view presets (scene rotation, not the camera itself).
        // POSITIVE rotationX tips the scene so the viewer looks DOWN at the
        // chair from above (negative shows it from below); Top sits exactly
        // on the +PI/2 clamp of SceneRotationController.maxRotationX.
        const views = [
            { icon: ICONS.square, label: 'Side', ...VIEW_PRESETS.side, tooltip: 'Side view - the drawn profile (Space)' },
            { icon: ICONS.squareSplitHorizontal, label: 'Front', ...VIEW_PRESETS.front, tooltip: 'Front view - across the chair' },
            { icon: ICONS.squareHalf, label: 'Top', ...VIEW_PRESETS.top, tooltip: 'Top view (plan)' },
            { icon: ICONS.cube, label: 'Iso', ...VIEW_PRESETS.iso, tooltip: 'Isometric view, chair front (Space)' }
        ];

        const viewsTitle = document.createElement('div');
        viewsTitle.textContent = 'Views';
        viewsTitle.className = 'props-field-label';
        panel.appendChild(viewsTitle);

        const viewsRow = document.createElement('div');
        viewsRow.style.cssText = `
            display: flex;
            align-items: stretch;
            gap: 4px;
        `;
        views.forEach(view => {
            const button = this.createControlButton({
                icon: view.icon,
                label: view.label,
                tooltip: view.tooltip,
                action: () => {
                    if (this.app.sceneRotation) {
                        this.app.sceneRotation.setRotation(view.x, view.y);
                    }
                }
            });
            viewsRow.appendChild(button);
        });
        panel.appendChild(viewsRow);

        // Info text (matches the actual bindings)
        const info = document.createElement('div');
        info.style.cssText = `
            color: #888;
            font-size: 11px;
            line-height: 1.5;
        `;
        info.innerHTML = `
            <div><strong>Right Drag:</strong> Rotate around chair</div>
            <div><strong>Middle Drag:</strong> Pan view</div>
            <div><strong>Scroll:</strong> Zoom</div>
            <div><strong>Arrow Keys:</strong> Rotate view</div>
        `;
        panel.appendChild(info);

        this.panel = panel;
    }

    /**
     * Create a control button (inline Phosphor SVG icon + label)
     */
    createControlButton(control) {
        const button = document.createElement('button');
        button.title = control.tooltip;
        button.className = 'sc-tool-btn';
        button.style.cssText = `
            background: transparent;
            border: 2px solid transparent;
            border-radius: 8px;
            padding: 8px 10px;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            flex: 1;
            color: #444;
        `;

        // Icon (inline SVG so currentColor tints it)
        button.appendChild(phIcon(control.icon, 20));

        // Label
        const label = document.createElement('div');
        label.textContent = control.label;
        label.style.cssText = `
            color: #666;
            font-size: 11px;
            font-weight: 500;
        `;
        button.appendChild(label);

        // Hover
        button.addEventListener('mouseenter', () => {
            button.style.background = 'rgba(200, 200, 200, 0.3)';
            label.style.color = '#000';
        });

        button.addEventListener('mouseleave', () => {
            button.style.background = 'transparent';
            label.style.color = '#666';
            // Re-apply the active highlight if this mode is armed
            this.update();
        });

        // Click
        button.addEventListener('click', control.action);

        return button;
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Update panel state - highlight the active camera mode button
     */
    update() {
        if (!this.modeButtons) return;
        for (const [mode, button] of this.modeButtons) {
            const active = this.app.activeCameraMode === mode;
            button.style.border = active ? '2px solid var(--accent)' : '2px solid transparent';
            button.style.background = active ? 'var(--accent-tint)' : 'transparent';
        }
    }
}
